import apiService from './apiService';

/**
 * API específica para el servicio de notas
 * Usa el servicio centralizado de API
 */
class NotesApi {

  /**
   * Obtener todas las notas
   * @returns {Promise<Array>} Lista de notas
   */
  async getAllNotes() {
    return apiService.getNotesByTag('all');
  }

  /**
   * Obtener notas por task específico
   * @param {string} taskId - ID del task
   * @returns {Promise<Array>} Lista de notas del task
   */
  async getNotesByTask(taskId) {
    // Nota: Este método necesita ser implementado en apiService si es necesario
    console.log(`📋 Fetching notes for task: ${taskId}`);
    throw new Error('Method not implemented yet');
  }

  /**
   * Obtener notas por tag específico
   * @param {string} tag - Tag de la nota (ej: "main-menu", "merchant", "LOAN_APPROVAL")
   * @returns {Promise<Array>} Lista de notas del tag
   */
  async getNotesByTag(tag) {
    console.log(`🏷️ Fetching notes for tag: ${tag}`);
    return apiService.getNotesByTag(tag);
  }

  /**
   * Obtener notas con paginación básica
   * @param {number} page - Número de página (default: 0)
   * @param {number} size - Tamaño de página (default: 10)
   * @param {string} sort - Campo de ordenamiento (default: 'id,desc')
   * @returns {Promise<Object>} Respuesta paginada con notas
   */
  async getNotesPaginated(page = 0, size = 10, sort = 'id,desc') {
    console.log(`📄 Fetching notes page ${page}, size ${size}, sort ${sort}`);
    return apiService.getNotesPaginated(page, size, sort);
  }

  /**
   * Búsqueda avanzada de notas con múltiples filtros
   * @param {Object} searchCriteria - Criterios de búsqueda
   * @param {string} searchCriteria.originType - Tipo de origen (merchant, application, step)
   * @param {string} searchCriteria.originId - ID del origen
   * @param {string|Array} searchCriteria.tag - Tag(s) para filtrar
   * @param {string} searchCriteria.createdBy - Usuario creador
   * @param {string} searchCriteria.dateFrom - Fecha desde (ISO string)
   * @param {string} searchCriteria.dateTo - Fecha hasta (ISO string)
   * @param {string} searchCriteria.search - Búsqueda en contenido del mensaje
   * @param {number} searchCriteria.page - Número de página (default: 0)
   * @param {number} searchCriteria.size - Tamaño de página (default: 10)
   * @param {string} searchCriteria.sort - Ordenamiento (default: 'createdDate,desc')
   * @returns {Promise<Object>} Respuesta paginada con notas filtradas
   */
  async searchNotes(searchCriteria = {}) {
    console.log(`🔍 Searching notes with criteria:`, searchCriteria);
    return apiService.searchNotes(searchCriteria);
  }

  /**
   * Crear una nueva nota
   * @param {object} noteData - Datos de la nota
   * @param {string} noteData.message - Mensaje de la nota
   * @param {string} noteData.tag - Tag de la nota (ej: "LOAN_APPROVAL", "main-menu")
   * @param {string} noteData.user - Usuario que crea la nota
   * @param {string} noteData.originType - Tipo de origen (merchant, application, step) - REQUERIDO
   * @param {string} noteData.originId - ID del origen - REQUERIDO
   * @param {Array} noteData.userTags - Tags creados por el usuario (opcional)
   * @param {Array} noteData.systemTags - Tags del sistema (MERCHANT, APPLICATION, DEAL, OWNER) - REQUERIDO
   * @returns {Promise<object>} Nota creada
   */
  async createNote(noteData) {
    // Validar campos requeridos
    if (!noteData.originType || !noteData.originId) {
      throw new Error('originType and originId are required fields');
    }

    const payload = {
      message: noteData.message,
      tag: noteData.tag,
      user: noteData.user,
      originType: noteData.originType,
      originId: noteData.originId,
      userTags: noteData.userTags || [],
      systemTags: noteData.systemTags || []
    };

    console.log('📝 Creating note with payload:', payload);
    return apiService.createNote(payload);
  }

  /**
   * Actualizar una nota existente
   * @param {string} noteId - ID de la nota
   * @param {object} noteData - Datos actualizados
   * @returns {Promise<object>} Nota actualizada
   */
  async updateNote(noteId, noteData) {
    return apiService.updateNote(noteId, noteData);
  }

  /**
   * Eliminar una nota (hard delete)
   * @param {string} noteId - ID de la nota
   * @returns {Promise<object>} Resultado de la eliminación
   */
  async deleteNote(noteId) {
    return apiService.deleteNote(noteId);
  }

  /**
   * Soft delete - Cambiar estado activo de una nota
   * @param {string} noteId - ID de la nota
   * @param {boolean} active - Estado activo (true/false)
   * @returns {Promise<object>} Resultado del cambio de estado
   */
  async softDeleteNote(noteId, active = false) {
    console.log(`🔄 Soft ${active ? 'activating' : 'deactivating'} note ${noteId}`);
    return apiService.softDeleteNote(noteId, active);
  }

  /**
   * Obtener una nota específica por ID
   * @param {string} noteId - ID de la nota
   * @param {string} token - Token de autenticación
   * @returns {Promise<object>} Nota específica
   */
  async getNoteById(noteId, token) {
    return this.get(`/${noteId}`, token);
  }
}

// Instancia singleton para usar en toda la aplicación
const notesApi = new NotesApi();

export default notesApi;
